import { Reducer } from 'redux';
import { set, flow, getOr } from 'lodash/fp';

import { Client, ClientActionTypes, ClientSection, ClientState, IncompleteDocumentBehaviour } from './types';
import { LoginActionTypes } from '../../../auth/login/store/types';

const DEFAULT_FREE_PAGES = 1000;
const DEFAULT_MAX_PAGES = 20000;

export const initialClient: Client = {
    clientName: '',
    description: '',
    users: [],
    clientDatasetIds: [],
    clientFeaturePermissionIds: [],
    clientOpinions: [],
    fewShotAgreementTypeIds: [],
    demoClient: 0,
    freePages: DEFAULT_FREE_PAGES,
    maxPages: DEFAULT_MAX_PAGES,
    monthlyTotalPages: 0,
    incompleteDocumentBehaviour: IncompleteDocumentBehaviour.VISIBLE_TO_ALL,
    clientTermsAndConditionsDocuments: []
};

export const INITIAL_STATE: ClientState = {
    modalOpen: false,
    clients: [],
    selectedClient: null,
    clientError: null,
    isSaving: false,
    confirmDeleteClient: null,
    deletingClient: false,
    availableClientFeaturePermissions: [],
    openClientSection: ClientSection.GENERAL,
    filesToUpload: [],
    isUploading: false,
    isDownloading: false,
    rejectedQuestions: [],
    rejectedQuestionsToForgive: [],
    isFetchingRejectedQuestions: false,
    selectedFewShotAgreementTypeId: null
};

export const clientReducer: Reducer<ClientState> = (state = INITIAL_STATE, { payload, type }): ClientState => {
    switch (type) {
        case ClientActionTypes.TOGGLE_CLIENTS_MODAL: {
            const selectedClient = payload.isOpen ? getOr(initialClient, 'client', payload) : null;
            return flow(
                set('modalOpen', payload.isOpen),
                set('selectedClient', selectedClient),
                set('openClientSection', ClientSection.GENERAL),
                set('filesToUpload', []),
                set('rejectedQuestionsToForgive', [])
            )(state);
        }
        case ClientActionTypes.FETCH_ALL_CLIENTS_SUCCESSFUL:
            return set('clients', payload, state);
        case ClientActionTypes.FETCH_ALL_CLIENTS_FAILED:
            return set('clientError', payload, state);
        case ClientActionTypes.UPDATE_CLIENT_DETAILS: {
            const { key, value } = payload;
            return set(`selectedClient.${key}`, value, state);
        }
        case ClientActionTypes.UPDATE_CLIENT_USERS: {
            const { users, clientId } = payload;
            const clients = state.clients.map(client => client.clientId === clientId ? { ...client, users } : client);
            return flow(
                set('clients', clients),
                set('selectedClient.users', users)
            )(state);
        }
        case ClientActionTypes.UPDATE_CLIENT_STARTED:
            return set('isSaving', true, state);
        case ClientActionTypes.UPDATE_CLIENT_SUCCESSFUL:
            return flow(
                set('isSaving', false),
                set('clients', payload),
                set('selectedClient', null),
                set('modalOpen', false)
            )(state);
        case ClientActionTypes.UPDATE_CLIENT_FAILED:
            return flow(
                set('isSaving', false),
                set('clientError', payload)
            )(state);
        case ClientActionTypes.TOGGLE_DELETE_CLIENT_CONFIRMATION_MODAL:
            return set('confirmDeleteClient', payload, state);
        case ClientActionTypes.DELETE_CLIENT_STARTED:
            return set('deletingClient', true, state);
        case ClientActionTypes.DELETE_CLIENT_SUCCESSFUL:
            return flow(
                set('clients', payload),
                set('deletingClient', false),
                set('confirmDeleteClient', null)
            )(state);
        case ClientActionTypes.DELETE_CLIENT_FAILED:
            return flow(
                set('clientError', payload),
                set('deletingClient', false)
            )(state);
        case ClientActionTypes.FETCH_CLIENT_FEATURE_PERMISSIONS_SUCCESSFUL:
            return set('availableClientFeaturePermissions', payload, state);
        case ClientActionTypes.SET_OPEN_CLIENT_SECTION:
            return set('openClientSection', payload, state);
        case ClientActionTypes.SET_UPLOAD_CLIENT_TERMS_AND_CONDITIONS_DOCUMENTS:
            return set('filesToUpload', payload, state);
        case ClientActionTypes.UPLOAD_TERMS_AND_CONDITIONS_DOCUMENT_STARTED:
            return set('isUploading', true, state);
        case ClientActionTypes.UPLOAD_TERMS_AND_CONDITIONS_DOCUMENT_SUCCESSFUL: {
            const { clientTermsAndConditionsDocuments, clientId } = payload;
            const clients = state.clients.map(client => client.clientId === clientId ? { ...client, clientTermsAndConditionsDocuments } : client);
            return flow(
                set('isUploading', false),
                set('selectedClient.clientTermsAndConditionsDocuments', clientTermsAndConditionsDocuments),
                set('clients', clients),
                set('filesToUpload', [])
            )(state);
        }
        case ClientActionTypes.UPLOAD_TERMS_AND_CONDITIONS_DOCUMENT_FAILED:
            return set('isUploading', false, state);
        case ClientActionTypes.DOWNLOAD_TERMS_AND_CONDITIONS_DOCUMENT_STARTED:
            return set('isDownloading', true, state);
        case ClientActionTypes.DOWNLOAD_TERMS_AND_CONDITIONS_DOCUMENT_SUCCESSFUL:
        case ClientActionTypes.DOWNLOAD_TERMS_AND_CONDITIONS_DOCUMENT_FAILED:
            return set('isDownloading', false, state);
        case ClientActionTypes.FETCH_REJECTED_CHAT_BOT_QUESTIONS_STARTED:
            return set('isFetchingRejectedQuestions', true, state);
        case ClientActionTypes.FETCH_REJECTED_CHAT_BOT_QUESTIONS_SUCCESSFUL:
            return flow(
                set('rejectedQuestions', payload),
                set('isFetchingRejectedQuestions', false)
            )(state);
        case ClientActionTypes.FETCH_REJECTED_CHAT_BOT_QUESTIONS_FAILED:
            return set('isFetchingRejectedQuestions', false, state);
        case ClientActionTypes.TOGGLE_QUESTION_TO_FORGIVE: {
            const { userId, questionId } = payload;
            const userIncluded = state.rejectedQuestionsToForgive.find(rejectedForUser => rejectedForUser.userId === userId);
            let updatedState = state.rejectedQuestionsToForgive;
            if (userIncluded) {
                const questionExits = userIncluded.questionIds.includes(questionId);
                const updatedQuestionIds = questionExits ? userIncluded.questionIds.filter(id => id !== questionId) : [...userIncluded.questionIds, questionId];
                updatedState = updatedState.map(rejectedForUser => rejectedForUser.userId === userId ? ({ userId, questionIds: updatedQuestionIds }) : rejectedForUser);
                if (updatedQuestionIds.length === 0) {
                    updatedState = updatedState.filter(rejectedForUser => rejectedForUser.userId !== userId);
                }
            } else {
                updatedState = [...updatedState, { userId, questionIds: [questionId] }];
            }
            return set('rejectedQuestionsToForgive', updatedState, state);
        }
        case ClientActionTypes.TOGGLE_ALL_REJECTED_QUESTIONS_FOR_USER: {
            const { userId, questionIds } = payload;
            const includedUser = state.rejectedQuestionsToForgive.find(rejectedForUser => rejectedForUser.userId === userId);
            let updatedState = state.rejectedQuestionsToForgive;
            if (includedUser) {
                const allQuestionsIncluded = (questionIds as number[]).every(id => includedUser.questionIds.includes(id));
                updatedState = updatedState.filter(rejectedForUser => rejectedForUser.userId !== userId);
                if (!allQuestionsIncluded) {
                    updatedState = [...updatedState, { userId, questionIds }];
                }
            } else {
                updatedState = [...updatedState, { userId, questionIds }];
            }
            return set('rejectedQuestionsToForgive', updatedState, state);
        }
        case ClientActionTypes.FORGIVE_REJECTED_CHAT_BOT_QUESTIONS_STARTED:
            return set('isFetchingRejectedQuestions', true, state);
        case ClientActionTypes.FORGIVE_REJECTED_CHAT_BOT_QUESTIONS_SUCCESSFUL:
            return flow(
                set('rejectedQuestions', payload),
                set('isFetchingRejectedQuestions', false)
            )(state);
        case ClientActionTypes.FORGIVE_REJECTED_CHAT_BOT_QUESTIONS_FAILED:
            return set('isFetchingRejectedQuestions', false, state);
        case ClientActionTypes.SET_CLIENT_SELECTED_FEW_SHOT_AGREEMENT_TYPE_ID:
            return set('selectedFewShotAgreementTypeId', payload, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
